import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import AdminSidebar from "@/components/AdminSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Percent, Infinity as InfinityIcon, Clock, Gift, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { durationInMonths, isFreeAccessCode } from "@produce/shared";
import {
  DiscountCodeForm,
  EMPTY_DISCOUNT_CODE,
  normaliseDiscountCode,
  type NewDiscountCode,
} from "@/components/admin/DiscountCodeForm";

interface DiscountCode {
  id: string;
  code: string;
  discount_type: string;
  discount_value: number;
  usage_limit: number | null;
  usage_count: number;
  active: boolean;
  expires_at: string | null;
  created_at: string;
  duration_type: string;
  duration_months: number | null;
  grants_free_access: boolean;
}

export default function DiscountCodes() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newCode, setNewCode] = useState<NewDiscountCode>(EMPTY_DISCOUNT_CODE);
  const queryClient = useQueryClient();

  const { data: discountCodes = [] } = useQuery({
    queryKey: ["discount-codes"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("discount_codes")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        toast.error("Failed to load discount codes: " + error.message);
        throw error;
      }
      return data as DiscountCode[];
    },
  });

  const createCode = useMutation({
    mutationFn: async () => {
      const payload = normaliseDiscountCode(newCode);
      const { error } = await supabase.from("discount_codes").insert({
        ...payload,
        usage_limit: payload.usage_limit || null,
        expires_at: payload.expires_at || null,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["discount-codes"] });
      toast.success("Discount code created");
      setDialogOpen(false);
      setNewCode(EMPTY_DISCOUNT_CODE);
    },
    onError: (error) => {
      toast.error("Failed to create code: " + error.message);
    },
  });

  const toggleActive = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      const { error } = await supabase.from("discount_codes").update({ active }).eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["discount-codes"] });
      toast.success("Code status updated");
    },
    onError: (error) => {
      toast.error("Failed to update: " + error.message);
    },
  });

  const deleteCode = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("discount_codes").delete().eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["discount-codes"] });
      toast.success("Discount code deleted");
    },
    onError: (error) => {
      toast.error("Failed to delete: " + error.message);
    },
  });

  const formatDate = (date: string | null) => {
    if (!date) return "Never";
    return new Date(date).toLocaleDateString();
  };

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <AdminSidebar />
        <div className="flex-1 p-8">
          <div className="mx-auto max-w-7xl space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="mb-2 text-3xl font-bold">Discount Codes</h1>
                <p className="text-muted-foreground">
                  Create and manage subscription discount codes
                </p>
              </div>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="mr-2 h-4 w-4" />
                    New Code
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create Discount Code</DialogTitle>
                    <DialogDescription>Add a new discount code for subscriptions</DialogDescription>
                  </DialogHeader>
                  <DiscountCodeForm
                    value={newCode}
                    onChange={setNewCode}
                    onSubmit={() => createCode.mutate()}
                    isPending={createCode.isPending}
                  />
                </DialogContent>
              </Dialog>
            </div>

            <Card className="p-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Discount</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Usage</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {discountCodes.map((code) => (
                    <TableRow key={code.id}>
                      <TableCell className="font-mono font-bold">{code.code}</TableCell>
                      <TableCell>
                        {isFreeAccessCode(code) ? (
                          <Badge variant="secondary" className="flex w-fit items-center gap-1">
                            <Gift className="h-3 w-3" />
                            Free access
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="flex w-fit items-center gap-1">
                            <Percent className="h-3 w-3" />
                            {code.discount_value}%
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="flex w-fit items-center gap-1">
                          {code.duration_type === "forever" ? (
                            <>
                              <InfinityIcon className="h-3 w-3" />
                              {isFreeAccessCode(code) ? "Forever" : "Lifetime"}
                            </>
                          ) : (
                            <>
                              <Clock className="h-3 w-3" />
                              {(() => {
                                const m = durationInMonths(code);
                                return m === 1 ? "1 month" : `${m} months`;
                              })()}
                            </>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {code.usage_count} / {code.usage_limit || "∞"}
                      </TableCell>
                      <TableCell>{formatDate(code.expires_at)}</TableCell>
                      <TableCell>
                        <Badge variant={code.active ? "default" : "secondary"}>
                          {code.active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              toggleActive.mutate({ id: code.id, active: !code.active })
                            }
                          >
                            {code.active ? "Deactivate" : "Activate"}
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => deleteCode.mutate(code.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
}
